import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Select, MenuItem, FormControl } from "@mui/material";
import { styled } from "@mui/material/styles";
import "./Welcome.css";
import startBg from "../assets/images/optimized/startBg.png";
import formBg from "../assets/images/optimized/formBg.png";
import troubleOtp from "../assets/images/optimized/troubleOtp.png";
import Header from "../components/Header";
import FirstRewardPopup from "../components/FirstRewardPopup";
import { firstRewardConfig } from "../data/firstRewardConfig";
import { db, functions, httpsCallable } from "../services/firebase";
import {
  collection,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  query,
  where,
  getDocs,
  deleteDoc,
} from "firebase/firestore";
import { useUser } from "../context/UserContext";

// Import avatar images
import avatar1 from "../assets/avatar/avatar1.png";
import avatar2 from "../assets/avatar/avatar2.png";
import avatar3 from "../assets/avatar/avatar3.png";
import avatar4 from "../assets/avatar/avatar4.png";
import avatar5 from "../assets/avatar/avatar5.png";
import avatar6 from "../assets/avatar/avatar6.png";
import avatar7 from "../assets/avatar/avatar7.png";
import avatar from "../assets/avatar/avatar.png";

// Styled Material UI components
const StyledSelect = styled(Select)({
  height: 50,
  backgroundColor: "#edf2f7",
  borderRadius: 12,
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiSelect-select": {
    padding: "12px 16px !important",
    color: "#1a202c",
    fontSize: "1rem",
    fontFamily:
      '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  "& .MuiSelect-icon": {
    color: "#1a202c",
    right: 16,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
});

const StyledMenuItem = styled(MenuItem)({
  fontSize: "1rem",
  color: "#1a202c",
  fontFamily:
    '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
});

const Welcome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    userData,
    updateUserData,
    setUserDocumentId,
    loadUserFromFirebase,
    userDocId,
    addCollectedTreasure,
  } = useUser();
  const [showAvatarSelection, setShowAvatarSelection] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    country: "",
    postcode: "",
    email: "",
    avatar: "",
  });
  const [otp, setOtp] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [showTroubleModal, setShowTroubleModal] = useState(false);
  const [showFirstReward, setShowFirstReward] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNewUser, setIsNewUser] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [postcodeError, setPostcodeError] = useState("");
  const [isPostcodeValid, setIsPostcodeValid] = useState(true);

  // Handle navigation state for direct OTP access
  useEffect(() => {
    if (location.state?.showOTP) {
      // If navigating from header with showOTP state, show OTP directly
      setShowOTP(true);
      setShowForm(true);
      // Pre-fill form with existing user data if available
      if (userData && userData.email) {
        setFormData((prev) => ({
          ...prev,
          name: userData.name || "",
          email: userData.email || "",
          country: userData.country || "",
          postcode: userData.postcode || "",
          avatar: userData.avatar || "",
        }));
        setSelectedAvatar(userData.avatar || null);
      }
    }
  }, [location.state, userData]);

  // Avatar options - using actual avatar images
  const avatarOptions = [
    { id: 1, image: avatar1 },
    { id: 2, image: avatar2 },
    { id: 3, image: avatar3 },
    { id: 4, image: avatar4 },
    { id: 5, image: avatar5 },
    { id: 6, image: avatar6 },
    { id: 7, image: avatar7 },
    { id: 8, image: avatar },
  ];

  // Log navigation state changes
  useEffect(() => {
    console.log("Welcome - Current location:", location.pathname);
    console.log("Welcome - History length:", window.history.length);
    console.log("Welcome - Location state:", location.state);
  }, [location]);

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleStart = () => {
    console.log("Welcome - handleStart called");
    setShowAvatarSelection(true);
  };

  const handleAvatarSelect = (avatar) => {
    setSelectedAvatar(avatar);
    setFormData((prev) => ({
      ...prev,
      avatar: avatar.image,
    }));
  };

  const handleAvatarNext = () => {
    if (selectedAvatar) {
      setShowAvatarSelection(false);
      setShowForm(true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear postcode error when user types (validation will happen on submit)
    if (name === "postcode") {
      setPostcodeError("");
      setIsPostcodeValid(true); // Reset validation state
    }
  };

  const handleSelectChange = (e, field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));

    // Clear postcode error when country changes (validation will happen on submit)
    if (field === "country" && formData.postcode.trim()) {
      setPostcodeError("");
      setIsPostcodeValid(true); // Reset validation state
    }
  };

  // Country code mapping for API
  const getCountryCode = (country) => {
    const countryCodeMap = {
      australia: "au",
      canada: "ca",
      india: "in",
      uk: "gb",
      usa: "us",
      germany: "de",
      france: "fr",
      italy: "it",
      spain: "es",
      netherlands: "nl",
      belgium: "be",
      switzerland: "ch",
      austria: "at",
      denmark: "dk",
      norway: "no",
      sweden: "se",
      finland: "fi",
      poland: "pl",
      "czech-republic": "cz",
      hungary: "hu",
      slovakia: "sk",
      slovenia: "si",
      croatia: "hr",
      serbia: "rs",
      bulgaria: "bg",
      romania: "ro",
      greece: "gr",
      portugal: "pt",
      ireland: "ie",
      japan: "jp",
      "south-korea": "kr",
      china: "cn",
      brazil: "br",
      argentina: "ar",
      chile: "cl",
      mexico: "mx",
      "new-zealand": "nz",
      "south-africa": "za",
      egypt: "eg",
      morocco: "ma",
      tunisia: "tn",
      algeria: "dz",
      turkey: "tr",
      ukraine: "ua",
      russia: "ru",
      kazakhstan: "kz",
      belarus: "by",
      moldova: "md",
      latvia: "lv",
      lithuania: "lt",
      estonia: "ee",
      iceland: "is",
      malta: "mt",
      luxembourg: "lu",
      monaco: "mc",
      liechtenstein: "li",
      andorra: "ad",
      "san-marino": "sm",
      "vatican-city": "va",
    };
    return countryCodeMap[country] || null;
  };

  // Postcode validation using free API
  const validatePostcode = async (postcode, country) => {
    if (!postcode || !country) return false;

    const countryCode = getCountryCode(country);
    if (!countryCode) {
      // If no country code mapping, accept any non-empty postcode
      return postcode.trim().length > 0;
    }

    try {
      const response = await fetch(
        `https://api.zippopotam.us/${countryCode}/${postcode.trim()}`
      );
      return response.ok; // true = valid, false = invalid
    } catch (error) {
      console.error("Error validating postcode:", error);
      return false;
    }
  };

  // Form validation function
  const isFormValid = () => {
    return (
      formData.name.trim() !== "" &&
      formData.email.trim() !== "" &&
      formData.country !== "" &&
      formData.postcode.trim() !== ""
    );
  };

  const handleSendOtp = async () => {
    if (!isFormValid()) {
      // Form validation error - let the UI handle it
      return;
    }

    // Validate postcode on submit
    if (formData.country && formData.postcode.trim()) {
      const isValid = await validatePostcode(
        formData.postcode,
        formData.country
      );
      setIsPostcodeValid(isValid);
      if (!isValid) {
        setPostcodeError(`Invalid postcode for ${formData.country}`);
        return;
      }
    }

    // Prevent double submission
    if (isSubmitting) {
      console.log("Already submitting, ignoring duplicate request");
      return;
    }

    setIsSubmitting(true);

    try {
      console.log("Starting OTP process for email:", formData.email);

      // 1. Check if user with same email already exists
      try {
        const emailQuery = query(
          collection(db, "users"),
          where("email", "==", formData.email)
        );
        const emailSnapshot = await getDocs(emailQuery);

        console.log(
          "Email query result - empty:",
          emailSnapshot.empty,
          "docs count:",
          emailSnapshot.docs.length
        );

        if (!emailSnapshot.empty) {
          // User with same email exists
          const existingUser = emailSnapshot.docs[0].data();

          if (existingUser.verified) {
            // User is already verified, load fresh data from Firebase
            console.log(
              "Verified user with same email exists, loading fresh data from Firebase"
            );

            const existingUserDoc = emailSnapshot.docs[0];
            setUserDocumentId(existingUserDoc.id);

            // Load fresh data from Firebase
            const updatedUserData = await loadUserFromFirebase(formData.email);
            if (!updatedUserData) {
              console.error("Failed to load user data from Firebase");
              setIsSubmitting(false);
              return;
            }

            setIsSubmitting(false);
            // Redirect existing users directly to home page
            setIsNewUser(false);
            navigate("/home");
            return;
          } else {
            // User exists but not verified, use existing user
            console.log(
              "Unverified user with same email exists, using existing user"
            );
            const existingUserDoc = emailSnapshot.docs[0];
            setUserDocumentId(existingUserDoc.id);
            console.log("Set userDocId for existing user:", existingUserDoc.id);

            // Continue with OTP flow using existing user
            try {
              const sendOtpEmail = httpsCallable(functions, "sendOtpEmail");
              const result = await sendOtpEmail({
                email: formData.email,
                userId: existingUserDoc.id,
              });
              console.log("OTP email sent to existing user:", result.data);

              setShowOTP(true);
              setResendTimer(30);
              setIsSubmitting(false);
              return;
            } catch (otpError) {
              console.error("Error sending OTP to existing user:", otpError);
              setIsSubmitting(false);
              return;
            }
          }
        }
      } catch (queryError) {
        console.error("Error checking for existing users:", queryError);
        setIsSubmitting(false);
        return;
      }

      // 2. Create user in Firestore with proper data structure
      console.log(
        "No existing user found, creating new user for email:",
        formData.email
      );

      const newUserData = {
        ...formData,
        avatar: selectedAvatar,
        createdAt: new Date(),
        verified: false,
        loginMethod: "otp",
        challengeScores: {},
        totalBoosterScore: 0,
        collectedTreasures: [],
      };

      const userRef = await addDoc(collection(db, "users"), newUserData);
      setUserDocumentId(userRef.id);
      console.log("Set userDocId for new user:", userRef.id);
      console.log("User created in Firestore with ID:", userRef.id);

      // 3. Call Firebase Function to send OTP email
      try {
        const sendOtpEmail = httpsCallable(functions, "sendOtpEmail");
        const result = await sendOtpEmail({
          email: formData.email,
          userId: userRef.id,
        });
        console.log("OTP email sent:", result.data);

        // 4. Show OTP screen only after successful email send
        setShowOTP(true);
        setResendTimer(30);
      } catch (otpError) {
        console.error("Error sending OTP to new user:", otpError);
        // Delete the user we just created since OTP failed
        try {
          await deleteDoc(doc(db, "users", userRef.id));
          console.log("Deleted user due to OTP failure:", userRef.id);
        } catch (deleteError) {
          console.error("Error deleting user after OTP failure:", deleteError);
        }
        setIsSubmitting(false);
        return;
      }
    } catch (error) {
      console.error("Error creating user or sending OTP:", error);
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (!userDocId) {
      // User not found - let the UI handle it
      return;
    }

    // Prevent double submission
    if (isResending) {
      return;
    }

    setIsResending(true);

    try {
      console.log("Resending OTP to email:", formData.email);

      // Call Firebase Function to resend OTP email
      const sendOtpEmail = httpsCallable(functions, "sendOtpEmail");
      const result = await sendOtpEmail({
        email: formData.email,
        userId: userDocId,
      });

      console.log("OTP email resent:", result.data);
      setResendTimer(30);

      // Success is handled by timer reset - no alert needed
    } catch (error) {
      console.error("Error resending OTP:", error);
      // Error is handled silently - user can try again
    } finally {
      setIsResending(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!userDocId) {
      // User not found - let the UI handle it
      return;
    }

    // Prevent double submission
    if (isVerifying) {
      return;
    }

    setIsVerifying(true);

    try {
      const userSnap = await getDoc(doc(db, "users", userDocId));
      if (!userSnap.exists()) {
        setIsVerifying(false);
        return;
      }

      const userData = userSnap.data();
      if (userData.otp === otp) {
        // Mark user as verified
        await updateDoc(doc(db, "users", userDocId), {
          verified: true,
          verifiedAt: new Date(),
        });

        // Load fresh data from Firebase and update context
        const updatedUserData = await loadUserFromFirebase(formData.email);
        if (!updatedUserData) {
          console.error("Failed to load verified user data from Firebase");
          setIsVerifying(false);
          return;
        }

        setShowFirstReward(true);
      } else {
        // Invalid OTP - let the UI handle it
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      // Verification failed - let the UI handle it
    } finally {
      setIsVerifying(false);
    }
  };

  const handleGuestLogin = async () => {
    console.log("Welcome - Guest login clicked, navigating to /hunt");
    console.log(
      "Welcome - History length before navigate:",
      window.history.length
    );

    try {
      // Check if user with same email already exists
      const emailQuery = query(
        collection(db, "users"),
        where("email", "==", formData.email)
      );
      const emailSnapshot = await getDocs(emailQuery);

      let userDocId;

      if (!emailSnapshot.empty) {
        // User exists, use existing user
        const existingUser = emailSnapshot.docs[0];
        userDocId = existingUser.id;
        console.log("Using existing guest user with ID:", userDocId);

        // Check if user is verified
        if (existingUser.data().verified) {
          // Existing verified user - redirect to home
          setUserDocumentId(userDocId);

          // Load fresh data from Firebase
          const updatedUserData = await loadUserFromFirebase(formData.email);
          if (!updatedUserData) {
            console.error("Failed to load existing user data from Firebase");
            return;
          }

          // Redirect existing users directly to home page
          setIsNewUser(false);
          navigate("/home");
          return;
        }
      } else {
        // Create new guest user in Firestore with proper data structure
        const newGuestData = {
          ...formData,
          avatar: selectedAvatar,
          verified: false,
          loginMethod: "guest",
          createdAt: new Date(),
          challengeScores: {},
          totalBoosterScore: 0,
          collectedTreasures: [],
        };

        const userRef = await addDoc(collection(db, "users"), newGuestData);
        userDocId = userRef.id;
        console.log("Created new guest user with ID:", userDocId);
      }

      // Set user document ID and update user data
      setUserDocumentId(userDocId);
      updateUserData({
        ...formData,
        avatar: selectedAvatar,
        verified: false,
        loginMethod: "guest",
      });

      // Navigate to hunt page for new users
      navigate("/hunt");
    } catch (error) {
      console.error("Error during guest login:", error);
      // Error during guest login - let the UI handle it
    }
  };

  const handleCollectReward = async () => {
    try {
      // Add the first reward to user's collected treasures
      await addCollectedTreasure("first-reward");

      // Close the popup and navigate based on user type
      setShowFirstReward(false);
      if (isNewUser) {
        navigate("/hunt");
      } else {
        navigate("/home");
      }

      setTimeout(() => {
        console.log(
          "Welcome - History length after navigate:",
          window.history.length
        );
      }, 100);
    } catch (error) {
      console.error("Error collecting first reward:", error);
      // Still navigate even if there's an error
      setShowFirstReward(false);
      if (isNewUser) {
        navigate("/hunt");
      } else {
        navigate("/home");
      }
    }
  };

  const renderOTPVerification = () => {
    return (
      <div className="otp-verification">
        <div className="otp-header">
          <h1>Enter One Time Password</h1>
          <p>
            We've sent a 6-digit code to your E-mail. Please enter it below to
            verify your identity.
          </p>
        </div>
        <div className="otp-input-group">
          <label>One Time Password</label>
          <input
            type="text"
            className="otp-input-field"
            placeholder="Enter OTP"
            value={otp}
            onChange={(e) =>
              setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            maxLength={6}
          />
        </div>
        <div className="otp-actions">
          <span className="resend-text">Didn't receive the code?</span>
          {resendTimer > 0 ? (
            <button className="resend-button" disabled>
              Resend in 00:{String(resendTimer).padStart(2, "0")} sec
            </button>
          ) : (
            <button
              className="resend-button"
              onClick={handleResendOtp}
              disabled={isResending}
            >
              {isResending ? "SENDING..." : "Resend OTP"}
            </button>
          )}
        </div>
        <button
          className="verify-button"
          onClick={handleVerifyOtp}
          disabled={isVerifying || otp.length !== 6}
        >
          {isVerifying ? "VERIFYING..." : "VERIFY"}
        </button>
        <button
          className="trouble-link"
          onClick={() => setShowTroubleModal(true)}
        >
          Trouble recieving OTP?
        </button>
      </div>
    );
  };

  const renderTroubleModal = () => {
    if (!showTroubleModal) return null;

    return (
      <div
        className="trouble-modal-overlay"
        onClick={() => setShowTroubleModal(false)}
      >
        <div className="trouble-modal" onClick={(e) => e.stopPropagation()}>
          <h2 className="trouble-modal-title">Trouble Receiving OTP?</h2>

          <div className="trouble-modal-illustration">
            <img
              src={troubleOtp}
              alt="Trouble OTP Illustration"
              className="trouble-illustration-img"
            />
          </div>

          <p className="trouble-modal-message">
            Oops! We're currently unable to send an OTP. You can proceed as a
            guest and verify your account later.
          </p>

          <div className="trouble-modal-actions">
            <button
              className="guest-login-button"
              onClick={() => {
                setShowTroubleModal(false);
                handleGuestLogin();
              }}
            >
              LOG IN AS A GUEST
            </button>
            <button
              className="retry-button"
              onClick={() => {
                setShowTroubleModal(false);
                handleResendOtp();
              }}
            >
              RETRY
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAvatarSelection = () => {
    return (
      <div className="avatar-selection">
        <div className="avatar-header">
          <h1>Let's set your look!</h1>
          <p>Choose how you'll appear on the leaderboard</p>
        </div>
        <div className="avatar-grid">
          {avatarOptions.map((avatar) => (
            <div
              key={avatar.id}
              className={`avatar-option ${
                selectedAvatar?.id === avatar.id ? "selected" : ""
              }`}
              onClick={() => handleAvatarSelect(avatar)}
            >
              <img
                src={avatar.image}
                alt={`Avatar ${avatar.id}`}
                className="avatar-image"
              />
              {selectedAvatar?.id === avatar.id && (
                <div className="avatar-tick-icon">✓</div>
              )}
            </div>
          ))}
        </div>
        <button
          className="avatar-next-button"
          onClick={handleAvatarNext}
          disabled={!selectedAvatar}
        >
          NEXT
        </button>
      </div>
    );
  };

  if (showAvatarSelection) {
    return (
      <div className="welcome-screen">
        <Header />
        <div className="form-background">
          <div className="form-image-container">
            <img src={formBg} alt="Background" />
          </div>
          <div className="center-card" style={{ height: `calc(100% - 30vh)` }}>
            <div className="form-content">{renderAvatarSelection()}</div>
          </div>
        </div>

        <FirstRewardPopup
          isOpen={showFirstReward}
          onClose={() => setShowFirstReward(false)}
          onCollect={handleCollectReward}
          rewardData={firstRewardConfig}
        />
      </div>
    );
  }

  if (showForm) {
    return (
      <div className="welcome-screen">
        <Header />
        <div className="form-background">
          <div className="form-image-container">
            <img src={formBg} alt="Background" />
          </div>
          <div
            className="center-card"
            style={{ height: showOTP ? `calc(100% - 30vh)` : "auto" }}
          >
            <div className="form-content">
              {showOTP ? (
                <>
                  {renderOTPVerification()}
                  {renderTroubleModal()}
                </>
              ) : (
                <>
                  <div className="form-welcome">
                    <h1>Welcome!</h1>
                    <p>
                      Fill in your details to start your augmented reality
                      treasure hunt adventure.
                    </p>
                  </div>
                  <form className="registration-form">
                    {renderTroubleModal()}
                    <div className="form-group">
                      <label>Name</label>
                      <input
                        type="text"
                        name="name"
                        placeholder="Your full name"
                        value={formData.name}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Email ID</label>
                      <input
                        type="email"
                        name="email"
                        placeholder="Your email ID"
                        value={formData.email}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Country</label>
                      <FormControl fullWidth>
                        <StyledSelect
                          value={formData.country}
                          onChange={(e) => handleSelectChange(e, "country")}
                          displayEmpty
                        >
                          <StyledMenuItem value="" disabled>
                            Country of origin
                          </StyledMenuItem>
                          <StyledMenuItem value="afghanistan">
                            Afghanistan
                          </StyledMenuItem>
                          <StyledMenuItem value="albania">
                            Albania
                          </StyledMenuItem>
                          <StyledMenuItem value="algeria">
                            Algeria
                          </StyledMenuItem>
                          <StyledMenuItem value="andorra">
                            Andorra
                          </StyledMenuItem>
                          <StyledMenuItem value="angola">Angola</StyledMenuItem>
                          <StyledMenuItem value="antigua-and-barbuda">
                            Antigua and Barbuda
                          </StyledMenuItem>
                          <StyledMenuItem value="argentina">
                            Argentina
                          </StyledMenuItem>
                          <StyledMenuItem value="armenia">
                            Armenia
                          </StyledMenuItem>
                          <StyledMenuItem value="australia">
                            Australia
                          </StyledMenuItem>
                          <StyledMenuItem value="austria">
                            Austria
                          </StyledMenuItem>
                          <StyledMenuItem value="azerbaijan">
                            Azerbaijan
                          </StyledMenuItem>
                          <StyledMenuItem value="bahamas">
                            Bahamas
                          </StyledMenuItem>
                          <StyledMenuItem value="bahrain">
                            Bahrain
                          </StyledMenuItem>
                          <StyledMenuItem value="bangladesh">
                            Bangladesh
                          </StyledMenuItem>
                          <StyledMenuItem value="barbados">
                            Barbados
                          </StyledMenuItem>
                          <StyledMenuItem value="belarus">
                            Belarus
                          </StyledMenuItem>
                          <StyledMenuItem value="belgium">
                            Belgium
                          </StyledMenuItem>
                          <StyledMenuItem value="belize">Belize</StyledMenuItem>
                          <StyledMenuItem value="benin">Benin</StyledMenuItem>
                          <StyledMenuItem value="bhutan">Bhutan</StyledMenuItem>
                          <StyledMenuItem value="bolivia">
                            Bolivia
                          </StyledMenuItem>
                          <StyledMenuItem value="bosnia-and-herzegovina">
                            Bosnia and Herzegovina
                          </StyledMenuItem>
                          <StyledMenuItem value="botswana">
                            Botswana
                          </StyledMenuItem>
                          <StyledMenuItem value="brazil">Brazil</StyledMenuItem>
                          <StyledMenuItem value="brunei">Brunei</StyledMenuItem>
                          <StyledMenuItem value="bulgaria">
                            Bulgaria
                          </StyledMenuItem>
                          <StyledMenuItem value="burkina-faso">
                            Burkina Faso
                          </StyledMenuItem>
                          <StyledMenuItem value="burundi">
                            Burundi
                          </StyledMenuItem>
                          <StyledMenuItem value="cambodia">
                            Cambodia
                          </StyledMenuItem>
                          <StyledMenuItem value="cameroon">
                            Cameroon
                          </StyledMenuItem>
                          <StyledMenuItem value="canada">Canada</StyledMenuItem>
                          <StyledMenuItem value="cape-verde">
                            Cape Verde
                          </StyledMenuItem>
                          <StyledMenuItem value="central-african-republic">
                            Central African Republic
                          </StyledMenuItem>
                          <StyledMenuItem value="chad">Chad</StyledMenuItem>
                          <StyledMenuItem value="chile">Chile</StyledMenuItem>
                          <StyledMenuItem value="china">China</StyledMenuItem>
                          <StyledMenuItem value="colombia">
                            Colombia
                          </StyledMenuItem>
                          <StyledMenuItem value="comoros">
                            Comoros
                          </StyledMenuItem>
                          <StyledMenuItem value="congo">Congo</StyledMenuItem>
                          <StyledMenuItem value="costa-rica">
                            Costa Rica
                          </StyledMenuItem>
                          <StyledMenuItem value="croatia">
                            Croatia
                          </StyledMenuItem>
                          <StyledMenuItem value="cuba">Cuba</StyledMenuItem>
                          <StyledMenuItem value="cyprus">Cyprus</StyledMenuItem>
                          <StyledMenuItem value="czech-republic">
                            Czech Republic
                          </StyledMenuItem>
                          <StyledMenuItem value="denmark">
                            Denmark
                          </StyledMenuItem>
                          <StyledMenuItem value="djibouti">
                            Djibouti
                          </StyledMenuItem>
                          <StyledMenuItem value="dominica">
                            Dominica
                          </StyledMenuItem>
                          <StyledMenuItem value="dominican-republic">
                            Dominican Republic
                          </StyledMenuItem>
                          <StyledMenuItem value="east-timor">
                            East Timor
                          </StyledMenuItem>
                          <StyledMenuItem value="ecuador">
                            Ecuador
                          </StyledMenuItem>
                          <StyledMenuItem value="egypt">Egypt</StyledMenuItem>
                          <StyledMenuItem value="el-salvador">
                            El Salvador
                          </StyledMenuItem>
                          <StyledMenuItem value="equatorial-guinea">
                            Equatorial Guinea
                          </StyledMenuItem>
                          <StyledMenuItem value="eritrea">
                            Eritrea
                          </StyledMenuItem>
                          <StyledMenuItem value="estonia">
                            Estonia
                          </StyledMenuItem>
                          <StyledMenuItem value="eswatini">
                            Eswatini
                          </StyledMenuItem>
                          <StyledMenuItem value="ethiopia">
                            Ethiopia
                          </StyledMenuItem>
                          <StyledMenuItem value="fiji">Fiji</StyledMenuItem>
                          <StyledMenuItem value="finland">
                            Finland
                          </StyledMenuItem>
                          <StyledMenuItem value="france">France</StyledMenuItem>
                          <StyledMenuItem value="gabon">Gabon</StyledMenuItem>
                          <StyledMenuItem value="gambia">Gambia</StyledMenuItem>
                          <StyledMenuItem value="georgia">
                            Georgia
                          </StyledMenuItem>
                          <StyledMenuItem value="germany">
                            Germany
                          </StyledMenuItem>
                          <StyledMenuItem value="ghana">Ghana</StyledMenuItem>
                          <StyledMenuItem value="greece">Greece</StyledMenuItem>
                          <StyledMenuItem value="grenada">
                            Grenada
                          </StyledMenuItem>
                          <StyledMenuItem value="guatemala">
                            Guatemala
                          </StyledMenuItem>
                          <StyledMenuItem value="guinea">Guinea</StyledMenuItem>
                          <StyledMenuItem value="guinea-bissau">
                            Guinea-Bissau
                          </StyledMenuItem>
                          <StyledMenuItem value="guyana">Guyana</StyledMenuItem>
                          <StyledMenuItem value="haiti">Haiti</StyledMenuItem>
                          <StyledMenuItem value="honduras">
                            Honduras
                          </StyledMenuItem>
                          <StyledMenuItem value="hungary">
                            Hungary
                          </StyledMenuItem>
                          <StyledMenuItem value="iceland">
                            Iceland
                          </StyledMenuItem>
                          <StyledMenuItem value="india">India</StyledMenuItem>
                          <StyledMenuItem value="indonesia">
                            Indonesia
                          </StyledMenuItem>
                          <StyledMenuItem value="iran">Iran</StyledMenuItem>
                          <StyledMenuItem value="iraq">Iraq</StyledMenuItem>
                          <StyledMenuItem value="ireland">
                            Ireland
                          </StyledMenuItem>
                          <StyledMenuItem value="israel">Israel</StyledMenuItem>
                          <StyledMenuItem value="italy">Italy</StyledMenuItem>
                          <StyledMenuItem value="ivory-coast">
                            Ivory Coast
                          </StyledMenuItem>
                          <StyledMenuItem value="jamaica">
                            Jamaica
                          </StyledMenuItem>
                          <StyledMenuItem value="japan">Japan</StyledMenuItem>
                          <StyledMenuItem value="jordan">Jordan</StyledMenuItem>
                          <StyledMenuItem value="kazakhstan">
                            Kazakhstan
                          </StyledMenuItem>
                          <StyledMenuItem value="kenya">Kenya</StyledMenuItem>
                          <StyledMenuItem value="kiribati">
                            Kiribati
                          </StyledMenuItem>
                          <StyledMenuItem value="kuwait">Kuwait</StyledMenuItem>
                          <StyledMenuItem value="kyrgyzstan">
                            Kyrgyzstan
                          </StyledMenuItem>
                          <StyledMenuItem value="laos">Laos</StyledMenuItem>
                          <StyledMenuItem value="latvia">Latvia</StyledMenuItem>
                          <StyledMenuItem value="lebanon">
                            Lebanon
                          </StyledMenuItem>
                          <StyledMenuItem value="lesotho">
                            Lesotho
                          </StyledMenuItem>
                          <StyledMenuItem value="liberia">
                            Liberia
                          </StyledMenuItem>
                          <StyledMenuItem value="libya">Libya</StyledMenuItem>
                          <StyledMenuItem value="liechtenstein">
                            Liechtenstein
                          </StyledMenuItem>
                          <StyledMenuItem value="lithuania">
                            Lithuania
                          </StyledMenuItem>
                          <StyledMenuItem value="luxembourg">
                            Luxembourg
                          </StyledMenuItem>
                          <StyledMenuItem value="madagascar">
                            Madagascar
                          </StyledMenuItem>
                          <StyledMenuItem value="malawi">Malawi</StyledMenuItem>
                          <StyledMenuItem value="malaysia">
                            Malaysia
                          </StyledMenuItem>
                          <StyledMenuItem value="maldives">
                            Maldives
                          </StyledMenuItem>
                          <StyledMenuItem value="mali">Mali</StyledMenuItem>
                          <StyledMenuItem value="malta">Malta</StyledMenuItem>
                          <StyledMenuItem value="marshall-islands">
                            Marshall Islands
                          </StyledMenuItem>
                          <StyledMenuItem value="mauritania">
                            Mauritania
                          </StyledMenuItem>
                          <StyledMenuItem value="mauritius">
                            Mauritius
                          </StyledMenuItem>
                          <StyledMenuItem value="mexico">Mexico</StyledMenuItem>
                          <StyledMenuItem value="micronesia">
                            Micronesia
                          </StyledMenuItem>
                          <StyledMenuItem value="moldova">
                            Moldova
                          </StyledMenuItem>
                          <StyledMenuItem value="monaco">Monaco</StyledMenuItem>
                          <StyledMenuItem value="mongolia">
                            Mongolia
                          </StyledMenuItem>
                          <StyledMenuItem value="montenegro">
                            Montenegro
                          </StyledMenuItem>
                          <StyledMenuItem value="morocco">
                            Morocco
                          </StyledMenuItem>
                          <StyledMenuItem value="mozambique">
                            Mozambique
                          </StyledMenuItem>
                          <StyledMenuItem value="myanmar">
                            Myanmar
                          </StyledMenuItem>
                          <StyledMenuItem value="namibia">
                            Namibia
                          </StyledMenuItem>
                          <StyledMenuItem value="nauru">Nauru</StyledMenuItem>
                          <StyledMenuItem value="nepal">Nepal</StyledMenuItem>
                          <StyledMenuItem value="netherlands">
                            Netherlands
                          </StyledMenuItem>
                          <StyledMenuItem value="new-zealand">
                            New Zealand
                          </StyledMenuItem>
                          <StyledMenuItem value="nicaragua">
                            Nicaragua
                          </StyledMenuItem>
                          <StyledMenuItem value="niger">Niger</StyledMenuItem>
                          <StyledMenuItem value="nigeria">
                            Nigeria
                          </StyledMenuItem>
                          <StyledMenuItem value="north-korea">
                            North Korea
                          </StyledMenuItem>
                          <StyledMenuItem value="north-macedonia">
                            North Macedonia
                          </StyledMenuItem>
                          <StyledMenuItem value="norway">Norway</StyledMenuItem>
                          <StyledMenuItem value="oman">Oman</StyledMenuItem>
                          <StyledMenuItem value="pakistan">
                            Pakistan
                          </StyledMenuItem>
                          <StyledMenuItem value="palau">Palau</StyledMenuItem>
                          <StyledMenuItem value="panama">Panama</StyledMenuItem>
                          <StyledMenuItem value="papua-new-guinea">
                            Papua New Guinea
                          </StyledMenuItem>
                          <StyledMenuItem value="paraguay">
                            Paraguay
                          </StyledMenuItem>
                          <StyledMenuItem value="peru">Peru</StyledMenuItem>
                          <StyledMenuItem value="philippines">
                            Philippines
                          </StyledMenuItem>
                          <StyledMenuItem value="poland">Poland</StyledMenuItem>
                          <StyledMenuItem value="portugal">
                            Portugal
                          </StyledMenuItem>
                          <StyledMenuItem value="qatar">Qatar</StyledMenuItem>
                          <StyledMenuItem value="romania">
                            Romania
                          </StyledMenuItem>
                          <StyledMenuItem value="russia">Russia</StyledMenuItem>
                          <StyledMenuItem value="rwanda">Rwanda</StyledMenuItem>
                          <StyledMenuItem value="saint-kitts-and-nevis">
                            Saint Kitts and Nevis
                          </StyledMenuItem>
                          <StyledMenuItem value="saint-lucia">
                            Saint Lucia
                          </StyledMenuItem>
                          <StyledMenuItem value="saint-vincent-and-the-grenadines">
                            Saint Vincent and the Grenadines
                          </StyledMenuItem>
                          <StyledMenuItem value="samoa">Samoa</StyledMenuItem>
                          <StyledMenuItem value="san-marino">
                            San Marino
                          </StyledMenuItem>
                          <StyledMenuItem value="sao-tome-and-principe">
                            São Tomé and Príncipe
                          </StyledMenuItem>
                          <StyledMenuItem value="saudi-arabia">
                            Saudi Arabia
                          </StyledMenuItem>
                          <StyledMenuItem value="senegal">
                            Senegal
                          </StyledMenuItem>
                          <StyledMenuItem value="serbia">Serbia</StyledMenuItem>
                          <StyledMenuItem value="seychelles">
                            Seychelles
                          </StyledMenuItem>
                          <StyledMenuItem value="sierra-leone">
                            Sierra Leone
                          </StyledMenuItem>
                          <StyledMenuItem value="singapore">
                            Singapore
                          </StyledMenuItem>
                          <StyledMenuItem value="slovakia">
                            Slovakia
                          </StyledMenuItem>
                          <StyledMenuItem value="slovenia">
                            Slovenia
                          </StyledMenuItem>
                          <StyledMenuItem value="solomon-islands">
                            Solomon Islands
                          </StyledMenuItem>
                          <StyledMenuItem value="somalia">
                            Somalia
                          </StyledMenuItem>
                          <StyledMenuItem value="south-africa">
                            South Africa
                          </StyledMenuItem>
                          <StyledMenuItem value="south-korea">
                            South Korea
                          </StyledMenuItem>
                          <StyledMenuItem value="south-sudan">
                            South Sudan
                          </StyledMenuItem>
                          <StyledMenuItem value="spain">Spain</StyledMenuItem>
                          <StyledMenuItem value="sri-lanka">
                            Sri Lanka
                          </StyledMenuItem>
                          <StyledMenuItem value="sudan">Sudan</StyledMenuItem>
                          <StyledMenuItem value="suriname">
                            Suriname
                          </StyledMenuItem>
                          <StyledMenuItem value="sweden">Sweden</StyledMenuItem>
                          <StyledMenuItem value="switzerland">
                            Switzerland
                          </StyledMenuItem>
                          <StyledMenuItem value="syria">Syria</StyledMenuItem>
                          <StyledMenuItem value="taiwan">Taiwan</StyledMenuItem>
                          <StyledMenuItem value="tajikistan">
                            Tajikistan
                          </StyledMenuItem>
                          <StyledMenuItem value="tanzania">
                            Tanzania
                          </StyledMenuItem>
                          <StyledMenuItem value="thailand">
                            Thailand
                          </StyledMenuItem>
                          <StyledMenuItem value="togo">Togo</StyledMenuItem>
                          <StyledMenuItem value="tonga">Tonga</StyledMenuItem>
                          <StyledMenuItem value="trinidad-and-tobago">
                            Trinidad and Tobago
                          </StyledMenuItem>
                          <StyledMenuItem value="tunisia">
                            Tunisia
                          </StyledMenuItem>
                          <StyledMenuItem value="turkey">Turkey</StyledMenuItem>
                          <StyledMenuItem value="turkmenistan">
                            Turkmenistan
                          </StyledMenuItem>
                          <StyledMenuItem value="tuvalu">Tuvalu</StyledMenuItem>
                          <StyledMenuItem value="uganda">Uganda</StyledMenuItem>
                          <StyledMenuItem value="ukraine">
                            Ukraine
                          </StyledMenuItem>
                          <StyledMenuItem value="united-arab-emirates">
                            United Arab Emirates
                          </StyledMenuItem>
                          <StyledMenuItem value="uk">
                            United Kingdom
                          </StyledMenuItem>
                          <StyledMenuItem value="usa">
                            United States
                          </StyledMenuItem>
                          <StyledMenuItem value="uruguay">
                            Uruguay
                          </StyledMenuItem>
                          <StyledMenuItem value="uzbekistan">
                            Uzbekistan
                          </StyledMenuItem>
                          <StyledMenuItem value="vanuatu">
                            Vanuatu
                          </StyledMenuItem>
                          <StyledMenuItem value="vatican-city">
                            Vatican City
                          </StyledMenuItem>
                          <StyledMenuItem value="venezuela">
                            Venezuela
                          </StyledMenuItem>
                          <StyledMenuItem value="vietnam">
                            Vietnam
                          </StyledMenuItem>
                          <StyledMenuItem value="yemen">Yemen</StyledMenuItem>
                          <StyledMenuItem value="zambia">Zambia</StyledMenuItem>
                          <StyledMenuItem value="zimbabwe">
                            Zimbabwe
                          </StyledMenuItem>
                        </StyledSelect>
                      </FormControl>
                    </div>
                    <div className="form-group">
                      <label>Postcode</label>
                      <input
                        type="text"
                        name="postcode"
                        placeholder="Your postcode"
                        value={formData.postcode}
                        onChange={handleInputChange}
                        style={{
                          border: postcodeError ? "2px solid red" : "none",
                          borderRadius: "12px",
                          padding: "12px 16px",
                          fontSize: "1rem",
                          width: "100%",
                          height: "50px",
                          background: "#edf2f7",
                          color: "#1a202c",
                          fontFamily:
                            '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      className="send-otp-button"
                      onClick={handleSendOtp}
                      disabled={!isFormValid() || isSubmitting || postcodeError}
                      style={{
                        opacity:
                          !isFormValid() || isSubmitting || postcodeError
                            ? 0.6
                            : 1,
                        cursor:
                          !isFormValid() || isSubmitting || postcodeError
                            ? "not-allowed"
                            : "pointer",
                      }}
                    >
                      {isSubmitting ? "SENDING..." : "SEND OTP"}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>

        <FirstRewardPopup
          isOpen={showFirstReward}
          onClose={() => setShowFirstReward(false)}
          onCollect={handleCollectReward}
          rewardData={firstRewardConfig}
        />
      </div>
    );
  }

  return (
    <div className="welcome-screen">
      <div className="welcome-background">
        <img src={startBg} alt="Background" className="bg-image" />
      </div>
      {/* <Header /> */}
      <div className="welcome-overlay">
        <div className="welcome-content">
          <div className="welcome-text">
            <h1>EXPERIENCE. PLAY. WIN.</h1>
            <h2>
              TCS SYDNEY MARATHON
              <br />
              PRESENTED BY ASICS
            </h2>
            <p>
              Explore Sydney In Augmented Reality. Collect Hidden Rewards.
              Unlock Instant Wins. And Rise To The Top Of The Leaderboard For
              Your Chance To Win $1,000.
            </p>
            {/* <div className="terms-link">
              <a href="#terms">T&Cs Apply.</a>
            </div> */}
          </div>
          <button className="start-button" onClick={handleStart}>
            START
          </button>
        </div>
      </div>

      <FirstRewardPopup
        isOpen={showFirstReward}
        onClose={() => setShowFirstReward(false)}
        onCollect={handleCollectReward}
        rewardData={firstRewardConfig}
      />
    </div>
  );
};

export default Welcome;
